<template>
    <div>
        <q-dialog v-model="prompt" class="consent-dialog">
            <q-card>
                <q-card-section>
                    <h5 class="consent-header">Privacy Consent</h5>
                </q-card-section>

                <q-separator />

                <q-card-section style="max-height: 50vh" class="scroll">
                    <p v-for="n in 1" :key="n">
                        I agree that any information I provide may be used for the purposes of case management and review,
                        including continuous assessment of risk and wellbeing. This information will be accessed by staff
                        directly involved with my care only.
                        Non-identifiable data will be used to assist Purapura Whetu Trust to improve their services.
                        I understand that my data will be held securely, will not be distributed to third parties without
                        prior consultation, and that I have rights under Privacy Act 2020 to access, change, and receive
                        further detail on how my data is being used.
                        I understand that when this information is no longer required for the agreed purposes, it will be
                        disposed of securely.
                    </p>
                </q-card-section>

                <q-separator />

                <q-card-actions class="consent-button-container" align="center">
                    <q-btn size="16px" outline flat no-caps rounded label="Decline" color="teal" v-close-popup />
                    <q-btn size="16px" outline flat no-caps rounded label="Accept" color="teal" :disabled="filterDisable"
                        @click="submitAgreement" v-close-popup v-show="myStep < 15" />
                </q-card-actions>
            </q-card>
        </q-dialog>

        <div class="form-header">
            <h4>Te Iti Kahurangi - Wellbeing and Safety Plan</h4>
            <h6 class="q-mt-md q-mb-md">
                The Te Iti Kahurangi forms have moved. They can now be found under the "Client Forms" section of the
                side menu
            </h6>
            <h6>Click <router-link :to="{ name: 'dynamicForm' }">here</router-link> to access the forms.</h6>
        </div>

        <div class="hidden">
            <div v-show="true">
                <h4>Te Iti Kahurangi Question Set</h4>
                <h5 class="q-mb-none">Client Information</h5>
                <h6 class="q-mt-md q-mb-md">
                    Please select a client before continuing. Where possible, outline any goals
                    the client wants to achieve to assist in reporting.
                </h6>
            </div>
        </div>

        <div class="form-card hidden">
            <ClientSelect v-model="selectedClient"></ClientSelect>

            <!-- Minimal history table below client select -->
            <div v-if="selectedClient" class="q-mt-md">
                <q-banner v-if="historyLoading" rounded class="bg-grey-2 text-grey-9 q-mb-sm rounded">
                    <q-spinner size="sm" rounded class="q-mr-sm" /> Loading form history…
                </q-banner>
                <q-banner v-else-if="historyError" class="bg-red-1 text-negative q-mb-sm">
                    {{ historyError }}
                </q-banner>
                <q-banner v-else-if="historyRows.length === 0" rounded class="bg-grey-2 text-grey-9 q-mb-sm ">
                    No historic forms found.
                </q-banner>
                <q-table
                    v-else
                    flat
                    bordered
                    dense
                    :rows="historyRows"
                    :columns="historyColumns"
                    row-key="__key"
                    :pagination="{ rowsPerPage: 10, sortBy: 'status', descending: true }"
                    @row-click="onRowClick"
                >
                    <template v-slot:body-cell-status="props">
                        <q-td :props="props">
                            <q-badge
                                :color="props.row.completedStatus === 'Completed' ? 'positive' : 'warning'"
                                :label="props.row.completedStatus"
                                outline
                                class="text-weight-medium"
                            />
                        </q-td>
                    </template>
                </q-table>
            </div>
            <div class="form-actions">
                <q-btn :disabled="!selectedClient" @click="prompt = true" label="Start new form" size="16px" no-caps flat
                    rounded outlined color="primary" />
            </div>
        </div>

    </div>
</template>

<script>
import ClientSelect from "../../ClientComponents/ClientSelectComponent.vue";
import api from "../../../services/api/api";

export default {
    name: "TeItiKahurangiStart",
    components: { ClientSelect },
    props: {
        modelValue: { type: Object, default: null }, // v-model for selectedClient
        myStep: { type: Number, default: 0 }
    },
    emits: ["update:modelValue", "accepted", "open-form"],
    data() {
        return {
            prompt: false,
            filterDisable: false,

            // history state
            historyLoading: false,
            historyError: null,
            historyRows: [],
            historyColumns: [
                { name: "completed", label: "Date", field: "date", align: "left", sortable: true },
                { name: "milestone", label: "Milestone", field: "milestone", align: "left", sortable: true },
                { name: "submittedBy", label: "Submitted By", field: "submittedBy", align: "left", sortable: true },
                { name: "status", field: "completedStatus", align: "left", sortable: true }
            ]
        };
    },
    computed: {
        selectedClient: {
            get() {
                return this.modelValue;
            },
            set(val) {
                this.$emit("update:modelValue", val);
            }
        }
    },
    watch: {
        selectedClient: {
            immediate: false,
            handler(val) {
                this.historyRows = [];
                this.historyError = null;
                if (val && val.client_id) {
                    this.fetchHistory(val.client_id);
                }
            }
        }
    },
    methods: {
        // Emit acceptance of privacy consent
        submitAgreement() {
            this.$emit("accepted");
        },

        // Fetch minimal history data for the selected client
        async fetchHistory(clientId) {
            this.historyLoading = true;
            this.historyError = null;
            try {
                const res = await api.forms.getTeitiKahurangiFormDataHistory(clientId);
                const data = Array.isArray(res?.data?.data) ? res.data.data : [];
                this.historyRows = data.map((r, idx) => ({
                    __key: `${idx}-${r?.submitted_time || ""}`,
                    formId: r?.form_id,
                    date: this.formatDate(r?.submitted_time),
                    milestone: r?.milestone || r?.form_milestone || "—",
                    submittedBy: r?.user_id || r?.submitted_by || "—",
                    completedStatus: r?.completed ? "Completed" : "In Progress"
                }));
            } catch (e) {
                this.historyError = "Failed to load form history.";
            } finally {
                this.historyLoading = false;
            }
        },

        // Simple date formatting utility
        formatDate(dateStr) {
            if (!dateStr) return "—";
            const d = new Date(dateStr);
            return isNaN(d.getTime()) ? "—" : d.toLocaleString('en-nz');
        },
        onRowClick(_, row) {
            if (row?.formId) {
                this.$emit("open-form", row.formId);
            }
        }
    }
};
</script>